import { LinkedAccount } from "src/gen/schema/bilge/api";
import { ChatPlatform } from "src/gen/schema/models/chatbot";

/**
 * Platform labels mapping
 */
const PLATFORM_LABELS: Partial<Record<ChatPlatform, string>> = {
  [ChatPlatform.PLATFORM_DISCORD]: "Discord",
  [ChatPlatform.PLATFORM_WEBEX]: "Webex",
  [ChatPlatform.PLATFORM_SLACK]: "Slack",
  [ChatPlatform.PLATFORM_MS_TEAMS]: "Microsoft Teams"
};

/**
 * Get the user-readable label for a chat platform
 */
export const getPlatformLabel = (platform: ChatPlatform): string => {
  return PLATFORM_LABELS[platform] || "Unknown";
};

/**
 * Get service-specific details from a linked account
 */
export const getServiceDetails = (binding: LinkedAccount): string => {
  if (binding.discordAccount) {
    const { guildName, channelName } = binding.discordAccount;
    return `Server: ${guildName || ""}; Channel: ${channelName || ""}`;
  }
  if (binding.webexAccount) {
    const { spaceName } = binding.webexAccount;
    return spaceName || "";
  }
  return "";
};

export type DeleteMessageParts = {
  readonly platformName: string;
  readonly channelName: string;
  readonly serverName: string;
  readonly channelLabel: string;
};

/**
 * Get the message parts for the delete binding confirmation modal.
 * Returns platform-specific channel/server names and labels.
 */
export const getDeleteMessageParts = (
  binding: LinkedAccount
): DeleteMessageParts => {
  const platformName = getPlatformLabel(binding.chatPlatform);

  if (binding.discordAccount) {
    return {
      platformName,
      channelName: binding.discordAccount.channelName || "",
      serverName: binding.discordAccount.guildName || "",
      channelLabel: "channel"
    };
  }

  if (binding.webexAccount) {
    return {
      platformName,
      channelName: binding.webexAccount.spaceName || "",
      serverName: "",
      channelLabel: "space"
    };
  }

  return {
    platformName,
    channelName: "",
    serverName: "",
    channelLabel: "channel"
  };
};

/**
 * Get detailed tooltip text for service details
 * Shows channel ID and guild ID for Discord, space ID for Webex
 */
export const getServiceDetailsTooltip = (binding: LinkedAccount): string => {
  if (binding.discordAccount) {
    const { guildId, channelId } = binding.discordAccount;
    return `GuildID: (${guildId || ""}); ChannelID: (${channelId || ""})`;
  }
  if (binding.webexAccount) {
    const { spaceId } = binding.webexAccount;
    return `Space ID: ${spaceId || ""}`;
  }
  return "";
};
